import { createContext, useContext, useRef, useState } from "react";
import { useSiteData } from "./SiteData";

/* Shared context. In the live site there is no provider, so useVE() returns a
   read-only default and <Editable> pulls published overrides from SiteData.
   In the admin visual editor a provider supplies the draft + edit handlers. */
export const VEContext = createContext(null);
export const useVE = () => useContext(VEContext) || { editMode: false };

const asNum = (v) => (v === 0 || v ? Number(v) : undefined);

/* Build responsive CSS (per-device offset + font-size) for the LIVE site so a
   phone tweak never changes the desktop layout. */
function buildResponsiveCss(key, ov) {
  const sel = `[data-ve="${key}"]`;
  const rule = (dev) => {
    const o = ov.offset?.[dev];
    const fs = asNum(ov.fontSize?.[dev]);
    const sc = asNum(ov.scale?.[dev]);
    const parts = [];
    const tx = [];
    if (o && (o.x || o.y)) tx.push(`translate(${o.x || 0}px,${o.y || 0}px)`);
    if (sc && sc !== 1) tx.push(`scale(${sc})`);
    if (tx.length) parts.push(`transform:${tx.join(" ")}`);
    if (sc && sc !== 1) parts.push(`transform-origin:left top`);
    if (fs) parts.push(`font-size:${fs}px`);
    return parts.length ? `${sel}{${parts.join(";")}}` : "";
  };
  let css = rule("desktop");
  const t = rule("tablet");
  if (t) css += `@media(max-width:1023px){${t}}`;
  const m = rule("mobile");
  if (m) css += `@media(max-width:639px){${m}}`;
  return css;
}

/* Styles shared across devices (colour, font, weight, alignment, spacing, bg). */
function globalStyle(base, ov) {
  const s = { ...base };
  if (ov.color) s.color = ov.color;
  if (ov.fontFamily) s.fontFamily = ov.fontFamily;
  if (ov.fontWeight) s.fontWeight = ov.fontWeight;
  if (ov.textAlign) s.textAlign = ov.textAlign;
  if (ov.lineHeight) s.lineHeight = ov.lineHeight;
  if (ov.letterSpacing || ov.letterSpacing === 0) s.letterSpacing = `${ov.letterSpacing}px`;
  if (ov.backgroundColor) s.backgroundColor = ov.backgroundColor;
  if (ov.padding || ov.padding === 0) s.padding = `${ov.padding}px`;
  if (ov.borderRadius || ov.borderRadius === 0) s.borderRadius = `${ov.borderRadius}px`;
  return s;
}

export const getOverride = (overrides, section, id) => overrides?.[section]?.ve?.[id] || {};

/* The one primitive that powers both the live site and the visual editor. */
export const Editable = ({ section, id, as = "div", className = "", children, onLive, style: base = {}, testid }) => {
  const ve = useVE();
  const { siteDesign } = useSiteData();
  const editMode = !!ve.editMode;
  const ov = getOverride(editMode ? ve.overrides : siteDesign, section, id);
  const key = `ve-${section}-${id}`;
  const Tag = as;
  const selKey = `${section}:${id}`;
  const selected = editMode && ve.selectedId === selKey;
  const [editing, setEditing] = useState(false);
  const elRef = useRef(null);

  const text = ov.text != null && ov.text !== "" ? ov.text : typeof children === "string" ? children : null;
  const content = text != null ? text : children;
  const inlineEditable = text != null; // only plain-text elements can be typed into

  // ---------- LIVE SITE ----------
  if (!editMode) {
    if (ov.hidden) return null;
    const css = buildResponsiveCss(key, ov);
    return (
      <>
        {css && <style dangerouslySetInnerHTML={{ __html: css }} />}
        <Tag data-ve={key} data-testid={testid} className={className} style={globalStyle(base, ov)} onClick={onLive}>
          {content}
        </Tag>
      </>
    );
  }

  // ---------- VISUAL EDITOR ----------
  const dev = ve.device || "desktop";
  const off = ov.offset?.[dev] || { x: 0, y: 0 };
  const fs = asNum(ov.fontSize?.[dev]);
  const sc = asNum(ov.scale?.[dev]) || 1;
  const estyle = {
    ...globalStyle(base, ov),
    transform: `translate(${off.x || 0}px, ${off.y || 0}px) scale(${sc})`,
    transformOrigin: "left top",
    ...(fs ? { fontSize: `${fs}px` } : {}),
    position: "relative",
    cursor: editing ? "text" : "move",
    outline: selected ? "2px solid #e11d2a" : "1px dashed rgba(225,29,42,.45)",
    outlineOffset: "2px",
    opacity: ov.hidden ? 0.4 : 1,
  };

  const startDrag = (e) => {
    if (editing) return;
    e.stopPropagation();
    ve.select(selKey);
    const sx = e.clientX, sy = e.clientY, ox = off.x || 0, oy = off.y || 0;
    const scale = ve.scale || 1;
    const move = (ev) => {
      ve.updateEl(section, id, { offset: { ...(ov.offset || {}), [dev]: { x: Math.round(ox + (ev.clientX - sx) / scale), y: Math.round(oy + (ev.clientY - sy) / scale) } } });
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  return (
    <Tag
      ref={elRef}
      data-ve={key}
      data-testid={testid}
      className={className}
      style={estyle}
      onPointerDown={startDrag}
      onClick={(e) => { e.stopPropagation(); e.preventDefault(); ve.select(selKey); }}
      onDoubleClick={inlineEditable ? (e) => { e.stopPropagation(); ve.select(selKey); setEditing(true); setTimeout(() => elRef.current?.focus(), 0); } : undefined}
      contentEditable={editing && inlineEditable}
      suppressContentEditableWarning
      onBlur={(e) => { if (editing && inlineEditable) { setEditing(false); ve.updateEl(section, id, { text: e.target.innerText }); } }}
    >
      {content}
    </Tag>
  );
};
